// Phase 10 (item 1 - "no approved item still a placeholder"): this was left
// as a ComingSoon stub since Phase 4, but the reporting layer it was
// waiting on (Phase 8's General Ledger, which already accepts any ledgerId)
// has existed since Phase 8. A per-vendor statement is exactly a General
// Ledger scoped to that Vendor's own dedicated 1:1 ledger (Phase 4/5
// correction: every Vendor has exactly one ledger, never a shared "Vendor
// Payable" control account) - so this reuses that same endpoint rather than
// inventing a second reporting code path.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "./shared";
import SearchableSelect from "./SearchableSelect";
import { ACCOUNTING_BASE } from "./accountingNav";
import { formatCurrency, formatBalance, formatDateDMY } from "./reports/reportShared";

export default function VendorLedgerPage() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/engine/vendors");
        setVendors(Array.isArray(res.data) ? res.data : []);
      } catch { setVendors([]); }
    })();
  }, []);

  const vendorOptions = useMemo(() => vendors.filter((v) => v.linkedLedgerId).map((v) => ({ value: v.id, label: v.vendorName, sublabel: v.category || "" })), [vendors]);
  const selectedVendor = vendors.find((v) => v.id === vendorId);

  const load = async () => {
    if (!selectedVendor?.linkedLedgerId) { setData(null); return; }
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/general-ledger", { params: { ledgerId: selectedVendor.linkedLedgerId, fromDate, toDate } });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load vendor ledger"));
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [vendorId, fromDate, toDate]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Vendor Ledger</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Statement-style, date-wise transactions for a single vendor, derived from that vendor's own dedicated ledger and posted vouchers.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-sm border border-[#E5E7EB] bg-white p-4">
        <div className="w-72 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Vendor</div>
          <SearchableSelect value={vendorId} onChange={setVendorId} options={vendorOptions} placeholder="Select vendor" />
        </div>
        <div className="w-40 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">From Date</div>
          <input type="date" className="h-9 w-full rounded-sm border border-[#D1D5DB] px-2 text-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="w-40 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">To Date</div>
          <input type="date" className="h-9 w-full rounded-sm border border-[#D1D5DB] px-2 text-sm" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
      </div>

      {!vendorId ? (
        <EmptyState>Select a vendor to view their ledger statement.</EmptyState>
      ) : !selectedVendor?.linkedLedgerId ? (
        <EmptyState>This vendor has no linked accounting ledger yet.</EmptyState>
      ) : loading ? (
        <div className="text-sm text-[#6B7280]">Loading...</div>
      ) : !data ? (
        <EmptyState>No data.</EmptyState>
      ) : (
        <SectionCard title={selectedVendor.vendorName} subtitle={`${data.fromDate} to ${data.toDate}`}>
          <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Opening</div><div className="mt-0.5 text-sm font-bold">{formatBalance(data.openingBalance, data.openingSide)}</div></div>
            <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Period Debit</div><div className="mt-0.5 text-sm font-bold">{formatCurrency(data.periodDebit)}</div></div>
            <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Period Credit</div><div className="mt-0.5 text-sm font-bold">{formatCurrency(data.periodCredit)}</div></div>
            <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Closing</div><div className="mt-0.5 text-sm font-bold">{formatBalance(data.closingBalance, data.closingSide)}</div></div>
          </div>
          <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead><TableHead>Voucher No.</TableHead><TableHead>Type</TableHead>
                  <TableHead>Particulars</TableHead><TableHead>Reference</TableHead>
                  <TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Running Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="bg-[#F9FAFB]">
                  <TableCell>Opening</TableCell><TableCell colSpan={2}>Opening Balance</TableCell><TableCell colSpan={2}>—</TableCell>
                  <TableCell className="text-right">—</TableCell><TableCell className="text-right">—</TableCell>
                  <TableCell className="text-right font-semibold">{formatBalance(data.openingBalance, data.openingSide)}</TableCell>
                </TableRow>
                {data.rows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{formatDateDMY(row.voucherDate)}</TableCell>
                    <TableCell>
                      {row.voucherId ? <button type="button" className="font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${row.voucherId}`)}>{row.voucherNumber || "(draft)"}</button> : "-"}
                    </TableCell>
                    <TableCell>{row.voucherType}</TableCell>
                    <TableCell>{row.narration}</TableCell>
                    <TableCell>{row.referenceNo || "-"}</TableCell>
                    <TableCell className="text-right">{row.debit ? formatCurrency(row.debit) : "—"}</TableCell>
                    <TableCell className="text-right">{row.credit ? formatCurrency(row.credit) : "—"}</TableCell>
                    <TableCell className="text-right font-semibold">{formatBalance(row.runningBalance, row.runningSide)}</TableCell>
                  </TableRow>
                ))}
                {data.rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-[#6B7280]">No transactions in this range.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
